// Internal: scan a Markdown source into positioned items (headings, component fences, prose runs).
// parse.ts, ids.ts and patch.ts all work from these offsets so edits stay byte-exact.
import { unified } from "unified";
import remarkParse from "remark-parse";
import remarkGfm from "remark-gfm";
import { toString } from "mdast-util-to-string";
import { parseInfoString } from "./infostring.js";
import { isComponentName } from "./schemas.js";
const processor = unified().use(remarkParse).use(remarkGfm).freeze();
export function parseMdast(text) {
    return processor.parse(text);
}
const HEADING_MARKER = /[ \t]*\{#([^}\s]*)\}$/;
const HEADING_MARKER_IN_TEXT = /\s*\{#[^}\s]*\}\s*$/;
function offsets(node) {
    const p = node.position;
    if (!p || p.start.offset === undefined || p.end.offset === undefined) {
        throw new Error("mdast node without position offsets");
    }
    return [p.start.offset, p.end.offset];
}
function scanHeading(node, full, s, e) {
    const raw = full.slice(s, e);
    let contentStart;
    let contentEnd;
    if (/^[ \t]{0,3}#/.test(raw)) {
        const open = /^[ \t]{0,3}#{1,6}/.exec(raw);
        contentStart = open[0].length;
        contentEnd = raw.length;
        // Closing sequence (`## Foo ##`): only when preceded by whitespace.
        const close = /[ \t]+#+[ \t]*$/.exec(raw);
        if (close && close.index >= contentStart)
            contentEnd = close.index;
        while (contentEnd > contentStart && /[ \t]/.test(raw[contentEnd - 1]))
            contentEnd--;
        if (contentEnd < contentStart)
            contentEnd = contentStart;
    }
    else {
        // Setext: the marker lives at the end of the last content line.
        contentStart = 0;
        const nl = raw.lastIndexOf("\n");
        contentEnd = nl < 0 ? raw.length : nl;
        while (contentEnd > 0 && /[ \t\r]/.test(raw[contentEnd - 1]))
            contentEnd--;
    }
    const content = raw.slice(contentStart, contentEnd);
    const m = HEADING_MARKER.exec(content);
    let id = "";
    let markerSpan = null;
    if (m) {
        id = m[1];
        markerSpan = { start: s + contentStart + m.index, end: s + contentEnd };
    }
    const text = toString(node).replace(HEADING_MARKER_IN_TEXT, "").trim();
    return {
        kind: "heading",
        start: s,
        end: e,
        depth: node.depth,
        text,
        id,
        markerSpan,
        insertAt: s + contentEnd,
    };
}
const OPEN_FENCE = /^([ \t]{0,3})(`{3,}|~{3,})[ \t]*/;
export function scanComponentFence(node, full, s, e, insideColumns) {
    const raw = full.slice(s, e);
    const nl = raw.indexOf("\n");
    const openLine = nl < 0 ? raw : raw.slice(0, nl);
    const fm = OPEN_FENCE.exec(openLine);
    if (!fm)
        return null;
    const infoStart = s + fm[0].length;
    const infoText = openLine.slice(fm[0].length);
    const info = parseInfoString(infoText);
    const name = info.name;
    const idTok = info.tokens.find((t) => t.key === "id" && !t.bare);
    if (!isComponentName(name) && !idTok)
        return null;
    const fenceChar = fm[2][0];
    const fenceLen = fm[2].length;
    const bodyStart = nl < 0 ? e : s + nl + 1;
    let bodyEnd = e;
    const lastNl = raw.lastIndexOf("\n");
    if (nl >= 0 && lastNl >= nl) {
        const lastLine = raw.slice(lastNl + 1);
        const cm = /^[ \t]{0,3}(`{3,}|~{3,})[ \t]*$/.exec(lastLine);
        if (cm && cm[1][0] === fenceChar && cm[1].length >= fenceLen)
            bodyEnd = s + lastNl;
    }
    if (bodyEnd < bodyStart)
        bodyEnd = bodyStart;
    const item = {
        kind: "component",
        start: s,
        end: e,
        name,
        info,
        infoStart,
        nameEnd: infoStart + info.nameEnd,
        idSpan: idTok ? { start: infoStart + idTok.start, end: infoStart + idTok.end } : null,
        id: idTok ? idTok.value : "",
        body: node.value,
        bodyStart,
        bodyEnd,
    };
    if (name === "columns" && !insideColumns) {
        item.children = scan(full, bodyStart, bodyEnd, true);
    }
    return item;
}
/** Scan `full[rangeStart, rangeEnd)`; all offsets in the result are absolute within `full`. */
export function scan(full, rangeStart = 0, rangeEnd = full.length, insideColumns = false) {
    const text = full.slice(rangeStart, rangeEnd);
    const root = parseMdast(text);
    const items = [];
    let run = null;
    const flush = () => {
        if (run)
            items.push(run);
        run = null;
    };
    for (const node of root.children) {
        const [ns, ne] = offsets(node);
        const s = rangeStart + ns;
        const e = rangeStart + ne;
        if (node.type === "heading") {
            flush();
            items.push(scanHeading(node, full, s, e));
            continue;
        }
        if (node.type === "code") {
            const comp = scanComponentFence(node, full, s, e, insideColumns);
            if (comp) {
                flush();
                items.push(comp);
                continue;
            }
        }
        if (run) {
            run.end = e;
            run.nodes.push(node);
        }
        else {
            run = { kind: "markdown", start: s, end: e, nodes: [node] };
        }
    }
    flush();
    return items;
}
/** Depth-first list of items (a columns item precedes its children). */
export function flattenItems(items) {
    const out = [];
    const walk = (list) => {
        for (const it of list) {
            out.push(it);
            if (it.kind === "component" && it.children)
                walk(it.children);
        }
    };
    walk(items);
    return out;
}
//# sourceMappingURL=scan.js.map